
import { map, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { CognitoUtil } from './cognito.service';
import { Prospectregister } from '../app/models/prospectregister'
import { ProspectTourInfo } from '../app/models/prospect-tour-info'
import { ProspectOpenhouse } from 'app/models/prospect-openhouse';
import { ProspectTourSummary } from 'app/models/prospect-tour-summary';
import { ProspectProfileInfo } from 'app/models/prospect-profile-info';
import { AgentShowingOption } from 'app/models/agent-showing-option';
import { ProspectTourCard } from 'app/models/prospect-tour-card';
import { ProspectConfirm } from 'app/models/prospect-confirm';
import { ProspectTourPaymentInfo } from 'app/models/prospect-tour-payment-info';
import { ProspectTourPayment } from 'app/models/prospect-tour-payment';
import { ProspectTourScheduleSelftour } from 'app/models/prospect-tour-schedule-selftour';
import { Router, ActivatedRoute } from '@angular/router';
import { ProspectTourLocation } from 'app/models/prospect-tour-location';
import { ProspectTourScheduleResult } from 'app/models/prospect-tour-schedule-result';
import { of, throwError } from 'rxjs';
import { Prospect } from 'app/models/prospect';
import { zoneTourStartTimes } from 'app/interface/zoneTourStartTimes';
import { VerifastIFrameResponse } from 'app/interface/verifastIframeResponse';
import { VerifastStatus } from 'app/interface/verifastStatus';
import * as luxon from 'luxon';
import * as moment from 'moment';

@Injectable()
export class ProspectService {
  public static _API_ENDPOINT = environment.apiEndPoint;

  constructor(private http: HttpClient, private cognitoUtil: CognitoUtil, private router: Router, private activatedRoute: ActivatedRoute) { }

  createOptions() {
    this.cognitoUtil.refresh();
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('AWSCreds'),
        'Identity': localStorage.getItem('CognitoId'),
        'Sub': localStorage.getItem('CognitoSub')
      })
    }
  }

  createUploadOptions() {
    this.cognitoUtil.refresh();
    return {
      headers: new HttpHeaders({
        'Accept': 'multipart/json',
        'Authorization': localStorage.getItem('AWSCreds'),
        'Identity': localStorage.getItem('CognitoId'),
        'Sub': localStorage.getItem('CognitoSub')
      })
    }
  }

  checkProspectRecord(email: String, providerType: String) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/CheckProspect', { "Email": email, "ProviderType": providerType }, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }
  
  getCognitoUserKey() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetCognitoUserKey', this.createOptions())
    .pipe(
      catchError(this.handleError),
      map((response) => <number>(response)));
  }

  getProspectInfo() {
    let headers: any;
    try {
      headers = this.createOptions();
    }
    catch (err) {
      return of(null);
    }

    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetProspectInfo', headers)
      .pipe(
        catchError(this.handleError),
        map((response: object) => <Prospect>(response))
      );
  }

  getTourInfo(yardiKey: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetProspectTourInfo?yardiKey=' + yardiKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourInfo>(response))
      );
  }

  getPropertyZone(yardiKey: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetZone?yardiKey=' + yardiKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <zoneTourStartTimes>(response))
      );
  }

  getOpenHouseOptions(yardiKey: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetOpenHouseOptions?yardiKey=' + yardiKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectOpenhouse[]>(response))
      );
  }

  selectOpenHouse(openHouse: ProspectOpenhouse) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/SelectOpenHouse', openHouse, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <number>(response))
      );
  }

  getOpenHouseSummary(prospectToOpenHouseKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetOpenHouseSummary?prospectToOpenHouseKey=' + prospectToOpenHouseKey, this.createOptions()).pipe(
      map((response) => <ProspectTourSummary>(response)));
  }

  getProspectProfileInfo() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetProspectProfileInfo', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectProfileInfo>(response))
      );
  }

  updateProspectProfileInfo(info: ProspectProfileInfo) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/UpdateProspectProfileInfo', info, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  getShowingOptions(yardiKey: string, date: Date) {
    return this.http.get(ProspectService._API_ENDPOINT + `Prospect/GetShowingOptions?yardiKey=${yardiKey}&date=${date.toISOString()}`, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <AgentShowingOption[]>(response))
      );
  }

  canUserStartTour(selfTourKey: number, date: string) {
    return this.http.get(ProspectService._API_ENDPOINT + `Prospect/CanProspectStartTour?selfTourKey=${selfTourKey}&userTime=${date}`, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  getIsPropertyOccupied(yardiKey: string) {
    return this.http.get(ProspectService._API_ENDPOINT + `Prospect/GetPropertyOccupied?yardiKey=${yardiKey}`)
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  createShowingForProspect(showing: AgentShowingOption) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/CreateShowingForProspect', showing, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <number>(response))
      );
  }

  getShowingSummary(showingKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetShowingSummary?showingKey=' + showingKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourSummary>(response))
      );
  }

  getOpenHousesForTourDash() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetOpenHousesForTourDash', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourCard[]>(response))
      );
  }

  getShowingsForTourDash() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetShowingsForTourDash', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourCard[]>(response))
      );
  }

  cancelShowing(showingKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/CancelShowing?showingKey=' + showingKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  cancelOpenHouse(openHouseRecordKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/CancelOpenHouse?openHouseRecordKey=' + openHouseRecordKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  getSelfTourCost() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetSelfTourCost', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <string>(response))
      );
  }

  getCostAndValidDays() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetCostandValidDays', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <string>(response))
      );
  }

  uploadDL(formData: FormData, yardiKey: string) {

    return this.http.post<ProspectTourScheduleResult>(ProspectService._API_ENDPOINT + 'Prospect/UploadDL?yardikey=' + yardiKey, formData, this.createUploadOptions())
      .pipe(
        catchError(this.handleError),
        map((res) => <ProspectTourScheduleResult>(res))
      );
  }

  GetCurrentUploadedLicense() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetCurrentUploadedLicense', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <string>(response))
      );
  }

  GetSelfTourConfirmInfo(additionalApplicantKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetSelfTourConfirmInfo?additionalApplicantKey=' + additionalApplicantKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectConfirm>(response))
      );
  }

  UpdateSelfTourConfirmInfo(info: ProspectConfirm, additionalApplicantKey: number, isApplication: boolean) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/UpdateSelfTourConfirmInfo?isApplication=' + isApplication +  '&additionalApplicantKey=' + additionalApplicantKey, info, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  GetSelfTourPaymentInfo(yardiKey: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetSelfTourPaymentInfo?yardiKey=' + yardiKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourPaymentInfo>(response))
      );
  }

  ProcessPayment(info: ProspectTourPayment) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/ProcessPayment', info, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => response)
      );
  }

  CheckForValidPayment() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/CheckForValidPayment', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  GetSelfTourDoorLockConfirm(yardiKey: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetSelfTourDoorLockConfirm?yardiKey=' + yardiKey).
      pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  ScheduleSelfTour(info: ProspectTourScheduleSelftour) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/ScheduleSelfTour', info, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourScheduleResult>(response))
      );
  }

  GetProspectSelfTourSummary(selfTourKey: String) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetProspectSelfTourSummary?selfTourKey=' + selfTourKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourSummary>(response))
      );
  }

  GetSelfToursForTourDash(userOffset: number, userDate: String) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/getSelfToursForTourDash?userOffset=' + userOffset + '&userDate=' + userDate, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <ProspectTourCard[]>(response))
      );
  }

  CheckForUploadedLicense() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/CheckForUploadedLicense', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  CheckForLocationAgreement() {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/CheckForLocationAgreement', this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  LocationAgreement() {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/LocationAgreement', null, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <string>(response))
      );
  }

  VerifyIdentity(formData: FormData) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/VerifyIdentity', formData, this.createUploadOptions())
      .pipe(
        catchError(this.handleError),
        map((res) => <String>(res))
      );
  }

  VerifyLocation(location: ProspectTourLocation) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/VerifyLocation', location, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  CreateProspectFromTourLogin(info: Prospectregister) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/CreateProspectFromTourLogin', info, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  SelfTourSchedulingStarted(yardiKey: string) {
    return this.http.post(ProspectService._API_ENDPOINT + 'Prospect/SelfTourSchedulingStarted?yardikey=' + encodeURIComponent(yardiKey), null, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  CheckProspectStatus() {
    this.http.get(ProspectService._API_ENDPOINT + 'Prospect/CheckProspectStatus', this.createOptions()).pipe(map((response) => <string>(response))).subscribe(res => this.router.navigate([`/tour/selftour/${res}`]));
  }

  GetVerifastURL(income: boolean, additionalApplicantKey: number, verifastIdWorkflow: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetVerifastURL?income=' + income + '&additionalApplicantKey=' + additionalApplicantKey + '&verifastIdWorkflow=' + verifastIdWorkflow, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <VerifastIFrameResponse>(response))
      );
  }

  GetVerifastStatus(income: boolean, additionalApplicantKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetVerifastStatus?income=' + income + '&additionalApplicantKey=' + additionalApplicantKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <VerifastStatus>(response))
      );
  }

  GetVerifastStatusBySessionId(income: boolean, sessionId: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/GetVerifastStatusBySessionId?income=' + income + '&sessionId=' + sessionId, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <string>(response))
      );
  }

  SetVerificationCompletedDate(sessionId: string) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/SetVerificationCompletedDate?sessionId=' + sessionId, this.createOptions())
    .pipe(
      catchError(this.handleError),
      map((response) => <boolean>(response))
    );
  }

  DenyOcrData(additionalApplicantKey: number) {
    return this.http.get(ProspectService._API_ENDPOINT + 'Prospect/DenyVerifastOcrData?additionalApplicantKey=' + additionalApplicantKey, this.createOptions())
      .pipe(
        catchError(this.handleError),
        map((response) => <boolean>(response))
      );
  }

  convertToLocalJSDate(date: string){
    const load = luxon.DateTime.fromISO(date, {zone:"utc"});
    const now = luxon.DateTime.fromObject({
      year: load.year,
      month: load.month,
      day: load.day
    });
    return now.toJSDate().toISOString();
  }

  convertToUTC(date: string) {
    if (date !== undefined) {
      var year: number;
      var month: number;
      var day: number;
      if (moment.isMoment(date)) {
        year = date.year();
        month = date.month() + 1; // moment months are zero based index
        day = date.date();
      }
  
      // Date is from backend or hasn't been chosen on front-end
      else if (typeof date === 'string') {
        const load = luxon.DateTime.fromISO(date);
        year = load.year,
        month = load.month,
        day = load.day
      }
  
      const utcDate = luxon.DateTime.fromObject({
        year: year,
        month: month,
        day: day
      }, { zone: "utc" });
      return utcDate.toISO();
    }
  }

  handleError(error: HttpErrorResponse | any) {
    let errMsg: string;
    if (error.status === 403) {
      this.router.navigate(['/logout']);
    }
    if (error.error) {
      const body = error.error;
      if (body.message) {
        errMsg = body.message;
      } else if (body.Message) {
        errMsg = body.Message;
      } else {
        errMsg = body ?? error.statusText;
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
      if (errMsg.length === 0) {
        errMsg = 'There was an unknown error.  Please try again later';
      }
    }
    return throwError(errMsg);
  }

}
